<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <h6>Installation Types</h6>
            <button class="btn btn-primary btn-sm" @click="openModal()">Add New Type</button>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Name</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                    <th class="text-secondary opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="type in installationTypes" :key="type.id">
                    <td><p class="text-xs font-weight-bold mb-0 px-3">{{ type.id }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ type.name || type.installation_type_name || type.installation_name || ('Type #' + type.id) }}</p></td>
                    <td class="align-middle text-center text-sm">
                      <span :class="type.status ? 'badge badge-sm bg-gradient-success' : 'badge badge-sm bg-gradient-secondary'">
                        {{ type.status ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="align-middle">
                      <button class="btn btn-link text-info text-gradient px-3 mb-0" @click="openModal(type)">
                        <i class="fas fa-pencil-alt me-2"></i>Edit
                      </button>
                      <button class="btn btn-link text-danger text-gradient px-3 mb-0" @click="deleteType(type.id)">
                        <i class="far fa-trash-alt me-2"></i>Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="modal-backdrop fade show"></div>
    <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? 'Edit' : 'Add' }} Installation Type</h5>
            <button type="button" class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Type Name (e.g. Medical, Media, Bank)</label>
              <input v-model="formData.name" type="text" class="form-control" placeholder="Enter name" />
            </div>
            
            <div class="form-group my-3">
              <label class="font-weight-bold mb-2">Select Modules</label>
              <div class="d-flex flex-wrap">
                <div class="form-check form-check-inline me-3 mb-2" v-for="mod in modules" :key="getModId(mod)">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'inst_mod_' + getModId(mod)" 
                    v-model="formData.module_ids"
                    :value="Number(getModId(mod))"
                  >
                  <label class="form-check-label text-sm" :for="'inst_mod_' + getModId(mod)">
                    {{ mod.module_name || mod.name || mod }}
                  </label>
                </div>
              </div>
            </div>

            <div class="form-group" v-if="editMode">
              <label>Status</label>
              <select v-model="formData.status" class="form-control">
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Close</button>
            <button type="button" class="btn btn-primary" @click="saveType">Save changes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "InstallationType",
  data() {
    return {
      installationTypes: [],
      modules: [],
      showModal: false,
      editMode: false,
      formData: { id: null, name: "", status: 1, module_ids: [] },
      apiUrl: (process.env.VUE_APP_API_BASE_URL) 
    };
  },
  mounted() {
    this.fetchTypes();
    this.fetchModules();
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem("token") || localStorage.getItem("auth_token") || localStorage.getItem("admin_token");
    },
    getModId(mod) {
      if (typeof mod === 'object' && mod !== null) {
        return mod.id || mod.module_id || mod;
      }
      return mod;
    },
    isModuleChecked(mod) {
      if (!Array.isArray(this.formData.module_ids)) return false;
      const modId = Number(this.getModId(mod));
      return this.formData.module_ids.map(Number).includes(modId);
    },
    toggleModuleCheck(mod) {
      if (!Array.isArray(this.formData.module_ids)) {
        this.formData.module_ids = [];
      }
      const modId = Number(this.getModId(mod));
      const currentIds = this.formData.module_ids.map(Number);
      const index = currentIds.indexOf(modId);
      if (index > -1) {
        currentIds.splice(index, 1);
      } else {
        currentIds.push(modId);
      }
      this.formData.module_ids = currentIds;
    },
    async fetchModules() {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(`${this.apiUrl}/v1/admin/get-modules`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const apiData = res.data;
        const apiModules = Array.isArray(apiData) ? apiData : (apiData?.data || apiData?.modules || []);
        
        this.modules = apiModules.filter(m => {
          const code = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return !['installationtype', 'tenants'].includes(code);
        });

        // Ensure Branches module is present for installation types if omitted by the database
        const hasBranchInList = this.modules.some(m => {
          const c = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return c.startsWith('branch') || c.startsWith('department');
        });
        if (!hasBranchInList) {
          this.modules.push({
            id: 6,
            module_id: 6,
            name: 'Branches',
            module_name: 'Branches',
            code: 'branches'
          });
        }

        console.log("Modules loaded dynamically for installation types:", this.modules);
      } catch (err) {
        console.error("Error fetching modules dynamically:", err);
        this.modules = [];
      }
    },
    async fetchTypes() {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(`${this.apiUrl}/v1/admin/installation-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data;
        if (Array.isArray(data)) {
          this.installationTypes = data;
        } else if (data && Array.isArray(data.data)) {
          this.installationTypes = data.data;
        } else if (data && Array.isArray(data.installationTypes)) {
          this.installationTypes = data.installationTypes;
        } else if (data && Array.isArray(data.result)) {
          this.installationTypes = data.result;
        } else {
          this.installationTypes = [];
        }
        console.log("Installation types loaded:", this.installationTypes);
      } catch (err) {
        console.error("Error fetching types", err);
      }
    },
    openModal(item = null) {
      if (item) {
        this.editMode = true;
        console.log("Opening edit modal for installation type item:", item);
        
        let rawList = item.module_ids || item.installation_type_modules || item.modules || item.selected_modules || item.permissions || [];
        if (typeof rawList === 'string') {
          try { rawList = JSON.parse(rawList); } catch (e) { rawList = []; }
        }

        const matchedIds = [];
        (Array.isArray(rawList) ? rawList : []).forEach(val => {
          let targetId = null;
          let targetCode = null;

          if (typeof val === 'number' || (typeof val === 'string' && !isNaN(val) && String(val).trim() !== '')) {
            targetId = Number(val);
          } else if (typeof val === 'string') {
            targetCode = val.toLowerCase().replace(/[^a-z0-9]/g, '');
          } else if (typeof val === 'object' && val !== null) {
            targetId = Number(val.id || val.module_id || (val.pivot && val.pivot.module_id));
            targetCode = String(val.code || val.module_name || val.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          }

          const found = (this.modules || []).find(m => {
            const mId = Number(this.getModId(m));
            const mCode = String(m.code || m.module_name || m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            if (targetId && mId === targetId) return true;
            if (targetCode && mCode === targetCode) return true;
            return false;
          });

          if (found) {
            matchedIds.push(Number(this.getModId(found)));
          } else if (targetId && !isNaN(targetId) && targetId > 0) {
            matchedIds.push(targetId);
          }
        });

        this.formData = {
          id: item.id,
          name: item.name || item.installation_type_name || '',
          status: item.status !== undefined ? item.status : 1,
          module_ids: Array.from(new Set(matchedIds))
        };
        console.log("Parsed formData.module_ids for modal:", this.formData.module_ids);
      } else {
        this.editMode = false;
        this.formData = { id: null, name: "", status: 1, module_ids: [] };
      }
      this.showModal = true;
    },
    async saveType() {
      const token = this.getAuthToken();
      try {
        const payload = { 
          ...this.formData,
          modules: this.formData.module_ids,
          module_ids: this.formData.module_ids
        };
        if (this.editMode) {
          await axios.put(`${this.apiUrl}/v1/admin/installation-types/update/${this.formData.id}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post(`${this.apiUrl}/v1/admin/installation-types/add`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        this.showModal = false;
        this.fetchTypes();
      } catch (err) {
        alert(err.response?.data?.message || "Operation failed");
      }
    },
    async deleteType(id) {
      if (!confirm("Are you sure?")) return;
      try {
        const token = this.getAuthToken();
        await axios.delete(`${this.apiUrl}/v1/admin/installation-types/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.fetchTypes();
      } catch (err) {
        alert("Error deleting record");
      }
    }
  }
};
</script>

<style scoped>
.modal-backdrop { z-index: 1040 !important; }
.modal { z-index: 1050 !important; }
</style>